import { useCallback, useMemo } from 'react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { SectionInfoIcon } from 'src/components/Feedback/SectionInfoIcon/SectionInfoIcon'
import { Logo } from 'src/icons'
import { GET_SMS_ACTIVITY_BY_EMPLOYEE } from 'src/graphql/smsAnalytics.graphql'
import AnalyticsCard from 'src/pages/AnalyticsPage/components/AnalyticsCard'
import {
  LeaderboardCardView,
  type LeaderboardColumn,
} from 'src/pages/AnalyticsPage/components/LeaderboardCardView'
import { useRefetchOnKeyChange } from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import type { SMSLogFiltersInput } from 'types/graphql'

interface SMSActivityByEmployeeRowShape {
  rowId: string
  rowName: string
  isSystemActor: boolean
  outbound: number
  inbound: number
}

interface TableRow extends SMSActivityByEmployeeRowShape {
  id: string
}

const UNASSIGNED_ROW_ID = 'unassigned'

const INFO_TEXT =
  'One row per employee with manual SMS, plus separate rows for Double Nickel system SMS and unassigned manual SMS. Click any column header to re-sort.'

const UNASSIGNED_INFO_TEXT =
  'Manual SMS not linked to any employee on the activity log.\n\nMost commonly inbound texts from candidates whose applicant has no assigned recruiter. Automated, bulk, and system SMS are counted under Double Nickel instead.'

const renderName = (row: TableRow) => (
  <div className="flex items-center gap-1.5">
    {row.isSystemActor && (
      <img
        src={Logo}
        alt=""
        aria-label="Double Nickel"
        className="h-3.5 w-3.5 shrink-0"
      />
    )}
    <span className={row.isSystemActor ? 'font-medium' : ''}>{row.rowName}</span>
    {row.rowId === UNASSIGNED_ROW_ID && (
      <SectionInfoIcon tooltip={UNASSIGNED_INFO_TEXT} />
    )}
  </div>
)

const renderCount = (n: number) =>
  n === 0 ? <span className="text-gray-400">0</span> : n.toLocaleString()

export interface SMSActivityByEmployeeContainerProps {
  filters: SMSLogFiltersInput
  refreshKey: number
}

const SMSActivityByEmployeeContainer = ({
  filters,
  refreshKey,
}: SMSActivityByEmployeeContainerProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_SMS_ACTIVITY_BY_EMPLOYEE, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const rows = useMemo<ReadonlyArray<TableRow>>(() => {
    const items =
      (data?.smsActivityByEmployee?.items as
        | ReadonlyArray<SMSActivityByEmployeeRowShape>
        | undefined) ?? []
    return items.map((row) => ({ ...row, id: row.rowId }))
  }, [data])

  const columns: ReadonlyArray<LeaderboardColumn<TableRow>> = [
    {
      key: 'rowName',
      label: 'Employee',
      align: 'left',
      render: renderName,
      sortable: true,
    },
    {
      key: 'outbound',
      label: 'Outbound',
      render: (r) => (
        <span className="font-medium">{renderCount(r.outbound)}</span>
      ),
    },
    {
      key: 'inbound',
      label: 'Inbound',
      render: (r) => renderCount(r.inbound),
    },
  ]

  const totals = useMemo(() => {
    if (rows.length === 0) return undefined
    const summed = rows.reduce(
      (acc, r) => ({
        outbound: acc.outbound + r.outbound,
        inbound: acc.inbound + r.inbound,
      }),
      { outbound: 0, inbound: 0 }
    )
    return {
      outbound: summed.outbound.toLocaleString(),
      inbound: summed.inbound.toLocaleString(),
    }
  }, [rows])

  return (
    <AnalyticsCard title="SMS Activity" info={INFO_TEXT}>
      <LeaderboardCardView<TableRow>
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage="No SMS in this period"
        defaultSortKey="outbound"
        defaultSortDir="desc"
        totals={totals}
      />
    </AnalyticsCard>
  )
}

export default SMSActivityByEmployeeContainer
